import { useEffect, useMemo, useRef } from 'react';
import swiperEffectFadeCss from 'swiper/css/effect-fade?raw';
import swiperScrollbarCss from 'swiper/css/scrollbar?raw';
import swiperCoreCss from 'swiper/css?raw';

import { useResizeObserver } from '@/hooks/use-resize-observer';
import useStore from '@/state';
import getRefByKey from '@/state/selectors/ref/get-ref-by-key';
import isCurrentWidthSmallerThan from '@/state/selectors/window/is-current-width-smaller-than';
import EntryPointsHelper from '@/utils/entry-points-helper';
import { Theme, css } from '@emotion/react';
import type { CSSObject } from '@emotion/serialize';
import { EnvVarsService } from '@multitenancy-spa/env-vars-service/esm/services/env-vars-service';

const classSelector = `[class^="${EnvVarsService.getVar('VITE_CACHE_KEY')}"]`;
const menuBarHeightCssVar = '--menu-bar-height';

const useGlobalStyle = (theme: Theme) => {
	const headerRef = useStore((state) => getRefByKey(state, 'header'));
	const entries = useResizeObserver(headerRef?.ref.current);
	const isSmallerThanDesktopSm = useStore((state) =>
		isCurrentWidthSmallerThan(state, theme.size.desktopSm),
	);
	const headerHeight = entries?.[0]?.target.clientHeight;
	const hasBreadcrumbs = !!document.querySelector(
		'.dxnav__breadcrumbs-container',
	);

	const paddingTop = !hasBreadcrumbs ? headerHeight : headerHeight + 55;

	const desktopPaddingTop = !hasBreadcrumbs
		? headerHeight
		: headerHeight + 44;

	const menuBarHeaderHeight = useMemo(() => {
		const menuBarHeightAsString =
			document.body.style.getPropertyValue(menuBarHeightCssVar);

		let menuBarHeightAsNumber = Number(
			menuBarHeightAsString?.replace('px', ''),
		);

		if (Number.isNaN(menuBarHeightAsNumber)) menuBarHeightAsNumber = 0;

		if (
			isSmallerThanDesktopSm &&
			paddingTop !== menuBarHeightAsNumber &&
			paddingTop !== undefined
		) {
			return paddingTop;
		} else if (
			!isSmallerThanDesktopSm &&
			desktopPaddingTop !== menuBarHeightAsNumber &&
			desktopPaddingTop !== undefined
		) {
			return desktopPaddingTop;
		}

		return 0;
	}, [desktopPaddingTop, isSmallerThanDesktopSm, paddingTop]);

	const menuBarHeightTimeout = useRef<NodeJS.Timeout | null>(null);

	useEffect(() => {
		if (menuBarHeightTimeout.current)
			clearTimeout(menuBarHeightTimeout.current);

		const timeoutId = setTimeout(() => {
			document.body.style.setProperty(
				menuBarHeightCssVar,
				`${menuBarHeaderHeight}px`,
			);
		}, 100);

		menuBarHeightTimeout.current = timeoutId;

		return () => {
			if (timeoutId) clearTimeout(timeoutId);
		};
	}, [menuBarHeaderHeight]);

	useEffect(() => {
		const originalSet = document.body.style.setProperty;
		document.body.style.setProperty = function (prop, val, ...args) {
			if (prop === menuBarHeightCssVar && val === '0px') return;
			return originalSet.call(this, prop, val, ...args);
		};
	}, [menuBarHeaderHeight]);

	const swiperCoreCssStyle = useMemo<CSSObject>(
		() => css`
			${swiperCoreCss.replaceAll(':first-child', ':first-of-type')}
		`,
		[],
	);
	const swiperEffectFadeCssStyle = useMemo<CSSObject>(
		() => css`
			${swiperEffectFadeCss.replaceAll(':first-child', ':first-of-type')}
		`,
		[],
	);
	const swiperScrollbarCssStyle = useMemo<CSSObject>(
		() => css`
			${swiperScrollbarCss.replaceAll(':first-child', ':first-of-type')}
		`,
		[],
	);

	const devStyle = useMemo<CSSObject>(() => ({ body: { margin: 0 } }), []);

	const normaliseStyle = useMemo<CSSObject>(
		() => ({
			[`${classSelector}`]: {
				boxSizing: 'border-box',
				WebkitTapHighlightColor: 'rgba(0, 0, 0, 0)',
				WebkitFontSmoothing: 'antialiased',
				'*:before, *:after': {
					WebkitTapHighlightColor: 'rgba(0, 0, 0, 0)',
				},
				[`h1${classSelector}, h2${classSelector}, h3${classSelector}, h4${classSelector}, h5${classSelector}, h6${classSelector}`]:
					{
						marginBlockStart: 0,
						marginBlockEnd: 0,
					},
				[`hr${classSelector}`]: {
					boxSizing: 'content-box',
					overflow: 'visible',
				},
				[`pre${classSelector}`]: {
					fontFamily: 'monospace, monospace',
					fontSize: '1em',
				},
				[`a${classSelector}`]: {
					textDecoration: 'none',
				},
				[`abbr[title]${classSelector}`]: {
					borderBottom: 'none',
					textDecoration: 'underline',
				},
				[`b${classSelector}, strong${classSelector}`]: {
					fontWeight: 'bolder',
				},
				[`small${classSelector}`]: {
					fontSize: '80%',
				},
				[`sub${classSelector}, sup${classSelector}`]: {
					fontSize: '75%',
					lineHeight: 0,
					position: 'relative',
					verticalAlign: 'baseline',
				},
				[`sub${classSelector}`]: {
					bottom: '-0.25em',
				},
				[`sup${classSelector}`]: {
					top: '-0.5em',
				},
				[`img${classSelector}`]: {
					borderStyle: 'none',
				},
				[`input${classSelector}, optgroup${classSelector}, select${classSelector}, textarea${classSelector}`]:
					{
						fontSize: '100%',
						lineHeight: 1.15,
						margin: 0,
					},
				[`input${classSelector}`]: {
					overflow: 'visible',
				},
				[`select${classSelector}`]: {
					textTransform: 'none',
				},
				[`button${classSelector}, [type="button"]${classSelector}, [type="reset"]${classSelector}, [type="submit"]${classSelector}`]:
					{
						WebkitAppearance: 'button',
					},
				[`button${classSelector}::-moz-focus-inner, [type="button"]${classSelector}::-moz-focus-inner, [type="reset"]${classSelector}::-moz-focus-inner, [type="submit"]${classSelector}::-moz-focus-inner`]:
					{
						borderStyle: 'none',
						padding: 0,
					},
				[`button${classSelector}:-moz-focusring, [type="button"]${classSelector}:-moz-focusring, [type="reset"]${classSelector}:-moz-focusring, [type="submit"]${classSelector}:-moz-focusring`]:
					{
						outline: '1px dotted ButtonText',
					},
				[`button${classSelector}`]: {
					cursor: 'pointer',
				},
				[`fieldset${classSelector}`]: {
					padding: '0.35em 0.75em 0.625em',
				},
				[`legend${classSelector}`]: {
					boxSizing: 'border-box',
					color: 'inherit',
					display: 'table',
					maxWidth: '100%',
					padding: 0,
					whiteSpace: 'normal',
				},
				[`progress${classSelector}`]: {
					verticalAlign: 'baseline',
				},
				[`textarea${classSelector}`]: {
					overflow: 'auto',
				},
				[`[type="checkbox"]${classSelector}, [type="radio"]${classSelector}`]:
					{
						boxSizing: 'border-box',
						padding: 0,
					},
				[`[type="number"]${classSelector}::-webkit-inner-spin-button, [type="number"]${classSelector}::-webkit-outer-spin-button`]:
					{
						height: 'auto',
					},
				[`[type="search"]${classSelector}`]: {
					WebkitAppearance: 'textfield',
					outlineOffset: '-2px',
				},
				[`[type="search"]${classSelector}::-webkit-search-decoration`]:
					{
						WebkitAppearance: 'none',
					},
				[`${classSelector}::-webkit-file-upload-button`]: {
					WebkitAppearance: 'button',
					font: 'inherit',
				},
				[`details${classSelector}`]: {
					display: 'block',
				},
				[`summary${classSelector}`]: {
					display: 'list-item',
				},
				[`template${classSelector}`]: {
					display: 'none',
				},
				[`${classSelector}[hidden]`]: {
					display: 'none',
				},
				[`ul${classSelector}`]: {
					marginBlockStart: 0,
					marginBlockEnd: 0,
					paddingInlineStart: 0,
				},
			},
		}),
		[],
	);

	useEffect(() => {
		const root = document.querySelector<HTMLElement>(':root');
		if (root) {
			let numericCurrentPaddingTop = 0;
			const currentPaddingTop = root.style.getPropertyValue(
				'--floating-action-button-top',
			);
			if (currentPaddingTop) {
				numericCurrentPaddingTop = Number(
					currentPaddingTop.replace('px', ''),
				);
			}

			const nextPaddingTop = isSmallerThanDesktopSm
				? paddingTop
				: desktopPaddingTop;

			if (nextPaddingTop > numericCurrentPaddingTop) {
				root.style.setProperty(
					'--floating-action-button-top',
					`${nextPaddingTop}px`,
				);
			}
		}
	}, [paddingTop, isSmallerThanDesktopSm, desktopPaddingTop]);

	const prodStyle = useMemo<CSSObject>(
		() => ({
			[`${classSelector}`]: {
				fontFamily: theme.font.families.primary.default,
				...normaliseStyle,
			},
			[`${EntryPointsHelper.entryPointSelector}.dxnav-header.stickyNavigation`]:
				{
					zIndex: 100,
				},
			'.cmp-floatingActionButton[data-visibility-desktop=true]': {
				[`${theme.media.desktop.laptop}`]: {
					top: 'var(--floating-action-button-top) !important',
				},
			},
		}),
		[
			theme.font.families.primary.default,
			theme.media.desktop.laptop,
			normaliseStyle,
		],
	);

	const style: CSSObject[] = [
		prodStyle,
		swiperCoreCssStyle,
		swiperEffectFadeCssStyle,
		swiperScrollbarCssStyle,
	];

	if (process.env.NODE_ENV === 'development') style.push(devStyle);

	const interpolatedStyle = [css(style)];

	return interpolatedStyle;
};

export default useGlobalStyle;
